import React, { useState, useEffect } from 'react';
import { API } from '../services/api';
import './ScheduledChangesListModal.css';

/**
 * Modal pour afficher les modifications planifiées et l'historique des versions
 */
function ScheduledChangesListModal({ isOpen, onClose, entityType = null, entityId = null, onLoadVersion }) {
    const [changes, setChanges] = useState([]);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'history'

    useEffect(() => {
        if (isOpen) {
            if (activeTab === 'pending') {
                loadChanges();
            } else {
                loadVersions();
            }
        }
    }, [isOpen, activeTab, entityType, entityId]);

    const loadChanges = async () => {
        setLoading(true);
        setError('');

        try {
            const filters = {};
            if (entityType) {
                filters.entity_type = entityType;
            }
            if (entityId) {
                filters.entity_id = entityId;
            }

            const response = await API.getScheduledChanges(filters);
            // La réponse peut être { scheduled_changes: [...] } ou { data: { scheduled_changes: [...] } }
            const changesData = response.scheduled_changes || response.data?.scheduled_changes || response.data || [];
            setChanges(Array.isArray(changesData) ? changesData : []);
        } catch (err) {
            setError(err.message || 'Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const loadVersions = async () => {
        if (!entityId || !entityType) {
            setVersions([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await API.getVersions(entityType, entityId);
            const versionsData = response.versions || response.data?.versions || response.data || [];
            setVersions(Array.isArray(versionsData) ? versionsData : []);
        } catch (err) {
            setError(err.message || 'Erreur lors du chargement de l\'historique');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (id) => {
        if (!window.confirm('Voulez-vous vraiment annuler cette modification planifiée ?')) {
            return;
        }

        try {
            await API.cancelScheduledChange(id);
            loadChanges(); // Recharger la liste
        } catch (err) {
            setError(err.message || 'Erreur lors de l\'annulation');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cette modification planifiée ?')) {
            return;
        }

        try {
            await API.deleteScheduledChange(id);
            loadChanges(); // Recharger la liste
        } catch (err) {
            setError(err.message || 'Erreur lors de la suppression');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: { label: 'En attente', className: 'badge-pending' },
            executed: { label: 'Exécutée', className: 'badge-executed' },
            cancelled: { label: 'Annulée', className: 'badge-cancelled' },
            failed: { label: 'Échouée', className: 'badge-failed' }
        };
        const badge = badges[status] || { label: status, className: '' };
        return <span className={`status-badge ${badge.className}`}>{badge.label}</span>;
    };

    const getChangeTypeLabel = (type) => {
        const labels = {
            update: 'Mise à jour',
            cascade_update: 'Mise à jour en cascade',
            activate: 'Activation',
            deactivate: 'Désactivation'
        };
        return labels[type] || type;
    };

    // Filtrer les modifications selon l'onglet actif
    const filteredChanges = changes.filter(change => {
        if (activeTab === 'pending') {
            return change.status === 'pending';
        } else {
            // history: executed, cancelled, failed
            return ['executed', 'cancelled', 'failed'].includes(change.status);
        }
    });

    // Compter les modifications par catégorie
    const pendingCount = changes.filter(c => c.status === 'pending').length;
    const historyCount = changes.filter(c => ['executed', 'cancelled', 'failed'].includes(c.status)).length;

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content scheduled-changes-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Versions</h2>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    {/* Onglets */}
                    <div className="tabs-bar">
                        <button
                            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                            onClick={() => setActiveTab('pending')}
                        >
                            En attente {pendingCount > 0 && <span className="tab-count">({pendingCount})</span>}
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                            onClick={() => setActiveTab('history')}
                        >
                            Historique {historyCount > 0 && <span className="tab-count">({historyCount})</span>}
                        </button>
                    </div>

                    {/* Contenu */}
                    {loading && (
                        <div className="loading-state">
                            Chargement...
                        </div>
                    )}

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {!loading && !error && activeTab === 'pending' && filteredChanges.length === 0 && (
                        <div className="empty-state">
                            <p>Aucune modification en attente</p>
                        </div>
                    )}

                    {!loading && !error && activeTab === 'history' && versions.length === 0 && (
                        <div className="empty-state">
                            <p>Aucune version disponible</p>
                        </div>
                    )}

                    {/* Onglet En attente - Modifications planifiées */}
                    {!loading && !error && activeTab === 'pending' && filteredChanges.length > 0 && (
                        <div className="changes-list">
                            {filteredChanges.map((change) => (
                                <div key={change.id} className="change-item">
                                    <div className="change-header">
                                        <div className="change-title">
                                            <strong>{change.description || 'Modification sans description'}</strong>
                                            {getStatusBadge(change.status)}
                                        </div>
                                        <div className="change-date">
                                            {formatDate(change.scheduled_at)}
                                        </div>
                                    </div>

                                    <div className="change-details">
                                        <div className="change-info">
                                            <span className="info-label">Type:</span>
                                            <span>{change.entity_type === 'signature' ? 'Signature' : 'Modèle'} #{change.entity_id}</span>
                                        </div>
                                        <div className="change-info">
                                            <span className="info-label">Action:</span>
                                            <span>{getChangeTypeLabel(change.change_type)}</span>
                                        </div>
                                        {change.executed_at && (
                                            <div className="change-info">
                                                <span className="info-label">Exécutée le:</span>
                                                <span>{formatDate(change.executed_at)}</span>
                                            </div>
                                        )}
                                        {change.error_message && (
                                            <div className="change-error">
                                                <span className="info-label">Erreur:</span>
                                                <span>{change.error_message}</span>
                                            </div>
                                        )}
                                    </div>

                                    {change.status === 'pending' && (
                                        <div className="change-actions">
                                            {onLoadVersion && (
                                                <button
                                                    className="btn-action btn-load"
                                                    onClick={() => {
                                                        if (!change.new_grid_data) {
                                                            alert('Les données de cette modification ne sont pas disponibles');
                                                            return;
                                                        }

                                                        try {
                                                            // Parser le JSON si c'est une string
                                                            const gridData = typeof change.new_grid_data === 'string'
                                                                ? JSON.parse(change.new_grid_data)
                                                                : change.new_grid_data;

                                                            // Charger les données de la modification planifiée dans l'éditeur
                                                            const versionData = {
                                                                grid_data: gridData,
                                                                container_width: 600, // Valeur par défaut
                                                                version_number: 'planifiée'
                                                            };
                                                            onLoadVersion(versionData);
                                                            onClose();
                                                        } catch (error) {
                                                            console.error('Erreur lors du parsing des données:', error);
                                                            alert('Erreur lors du chargement des données');
                                                        }
                                                    }}
                                                >
                                                    📝 Charger dans l'éditeur
                                                </button>
                                            )}
                                            <button
                                                className="btn-action btn-delete"
                                                onClick={() => handleDelete(change.id)}
                                            >
                                                Supprimer
                                            </button>
                                        </div>
                                    )}

                                    {(change.status === 'cancelled' || change.status === 'failed') && (
                                        <div className="change-actions">
                                            <button
                                                className="btn-action btn-delete"
                                                onClick={() => handleDelete(change.id)}
                                            >
                                                Supprimer
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Onglet Historique - Versions */}
                    {!loading && !error && activeTab === 'history' && versions.length > 0 && (
                        <div className="changes-list">
                            {versions.map((version) => (
                                <div key={version.id} className="change-item version-item">
                                    <div className="change-header">
                                        <div className="change-title">
                                            <strong>Version #{version.version_number}</strong>
                                            <span className="version-badge">v{version.version_number}</span>
                                        </div>
                                        <div className="change-date">
                                            {formatDate(version.created_at)}
                                        </div>
                                    </div>

                                    <div className="change-details">
                                        {version.comment && (
                                            <div className="change-info version-comment">
                                                <span className="info-label">Commentaire:</span>
                                                <span>{version.comment}</span>
                                            </div>
                                        )}
                                        {version.created_by_name && (
                                            <div className="change-info">
                                                <span className="info-label">Créée par:</span>
                                                <span>{version.created_by_name}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="change-actions">
                                        <button
                                            className="btn-action btn-load"
                                            onClick={() => {
                                                if (onLoadVersion) {
                                                    onLoadVersion(version);
                                                    onClose();
                                                }
                                            }}
                                        >
                                            📝 Charger dans l'éditeur
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ScheduledChangesListModal;
